import {getFromLocalStorage, saveToLocalStorage} from '@/utils/local.storage.utils';
import {type UrlParams, useUrlSearchParams} from '@vueuse/core';

const LOCAL_STORAGE_KEY = import.meta.env.VITE_URL_PARAMS_STORAGE_KEY;

/**
 * Convert URLSearchParams to object.
 *
 * @param urlSearchParams URLSearchParams to convert.
 */
const urlSearchParamsToObject = (urlSearchParams: URLSearchParams) => {
	const map: UrlParams = {};

	urlSearchParams.forEach((value, key) => {
		// add to values if key exists, else add new key
		if (map[key]) {
			const values = map[key];
			map[key] = Array.isArray(values) ? [...values, value] : [values, value];
		} else {
			map[key] = value;
		}
	});

	return map;
};

/**
 * Get initial params from query string as object.
 *
 * @param queryString Query string to get params from.
 * @returns Object with params.
 */
const getInitialParams = (queryString: string) => {
	const splitQueryString = queryString?.split('?');
	if (splitQueryString?.length > 1) {
		const urlSearchParams = new URLSearchParams(splitQueryString[1]);
		return urlSearchParamsToObject(urlSearchParams);
	}

	return null;
};

const getUrlParams = () => getInitialParams(window.location.hash);

const getUrlParamsFromLocalStorage = () => getInitialParams(getFromLocalStorage(LOCAL_STORAGE_KEY));

const initialParams = () => getUrlParams() ?? getUrlParamsFromLocalStorage() ?? {};

// This queryParams object is used to set initial, update and read params for back-end requests.
const queryParams: UrlParams = initialParams();

// useUrlSearchParams contains a bug causing it not to update the browser url when the value hasn't changed and is not an array.
// This happens when it's assigned initial params, the params won't be visible in the url.
// browserQueryParams is solely used to make params visible in the browser.
const browserQueryParams: UrlParams = useUrlSearchParams('hash');

/**
 * Get query string from params.
 *
 * @returns Query string.
 */
export const getUrlQueryString = () => {
	let urlParams = '';

	for (const [key, value] of Object.entries(queryParams)) {
		if (Array.isArray(value)) {
			urlParams += value.map(v => `&${key}=${v}`).join('');
		} else {
			urlParams += `&${key}=${value}`;
		}
	}

	return urlParams ? `?${urlParams.replace('&', '')}` : '';
};

/**
 * Add url param.
 *
 * @param key Key to use.
 * @param value Value to add.
 */
const addUrlParam = (key: string, value: string | string[] | number | boolean) => {
	queryParams[key] = Array.isArray(value) ? value : value.toString();

	// Always save value as array. Or else the browser url may not always update.
	browserQueryParams[key] = Array.isArray(value) ? value : [value.toString()];

	saveToLocalStorage(LOCAL_STORAGE_KEY, getUrlQueryString());
};

/**
 * Remove url param.
 *
 * @param key Key to remove.
 */
export const removeUrlParam = (key: string) => {
	delete queryParams[key];
	delete browserQueryParams[key];
	saveToLocalStorage(LOCAL_STORAGE_KEY, getUrlQueryString());
};

/**
 * Check if value is not empty.
 * Value is considered empty if it's undefined, null, empty string or empty array.
 *
 * @param value Value to check.
 */
const isEmpty = (value?: string | number | boolean | Array<any> | null) => {
	if (Array.isArray(value)) {
		return value?.length === 0;
	}

	return value === undefined || value === null || value === '';
};

/**
 * Parse value to correct type.
 *
 * @param value Value to parse.
 */
const parseValue = (value: string | string[]) => {
	if (value === 'true' || value === 'false') {
		// Convert to boolean.
		return JSON.parse(value);
	}

	return value ?? null;
}

/**
 * Get query value from url params.
 * The value returned is from the url if available or the url saved in local storage.
 *
 * @param key Key to get value for.
 */
export const getQueryParam = (key: string) => {
	return parseValue(queryParams[key]);
};

/**
 * Get query value from url params.
 *
 * @param key Key to get value for.
 */
export const getUrlParam = (key: string) => {
	const params = getUrlParams();

	if (!params) {
		return null;
	}

	return parseValue(params[key]);
};

/**
 * Get query value from url params saved in localstorage.
 *
 * @param key Key to get value for.
 */
export const getLocalStorageParam = (key: string) => {
	const params = getUrlParamsFromLocalStorage();

	if (!params) {
		return null;
	}

	return parseValue(params[key]);
};

/**
 * Update value in url.
 *
 * @param key Key to update value for.
 * @param value Value to update.
 */
export const updateUrlParam = (key: string, value?: string | number | boolean) => {
	value === undefined || isEmpty(value) ? removeUrlParam(key) : addUrlParam(key, value);
};

/**
 * Append value to url params.
 *
 * @param key Key to append value for.
 * @param value Value to append.
 */
export const appendToUrlParams = (key: string, value: string) => {
	const values = queryParams[key];

	if (Array.isArray(values) && !values.includes(value)) {
		addUrlParam(key, [...values, value]);
		return;
	}

	addUrlParam(key, [value]);
};

/**
 * Remove value from url params.
 *
 * @param key Key to remove value for.
 * @param value Value to remove.
 */
export const removeFromUrlParams = (key: string, value: string) => {
	const values = queryParams[key];

	if (Array.isArray(values)) {
		const newValues = values.filter((v: string) => v !== value);
		addUrlParam(key, newValues);
	} else {
		removeUrlParam(key);
	}
};

export const clearUrlParams = () => Object.keys(queryParams).forEach(key => removeUrlParam(key));
